import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import toast from 'react-hot-toast'
import { adminAuthService } from '../../../../shared/services/adminAuth'

// ── Types ────────────────────────────────────────────────────────

const RULE_TYPES = [
  { value: 'rate_limit', label: 'Rate Limiting' },
  { value: 'ip_block', label: 'IP Blocking' },
  { value: 'pattern_match', label: 'Pattern Matching' },
  { value: 'geo_block', label: 'Geographic Blocking' },
  { value: 'malware_detection', label: 'Malware Detection' },
  { value: 'phishing_protection', label: 'Phishing Protection' },
] as const

type RuleType = (typeof RULE_TYPES)[number]['value']

const ACTIONS = [
  { value: 'block', label: 'Block' },
  { value: 'warn', label: 'Warn' },
  { value: 'log', label: 'Log Only' },
  { value: 'redirect', label: 'Redirect' },
] as const

export interface SecurityRule {
  _id: string
  name: string
  description: string
  type: RuleType
  enabled: boolean
  config: Record<string, any>
  priority: number
  action: string
  redirectUrl?: string
  triggeredCount?: number
  lastTriggered?: string
  createdAt?: string
}

interface SecurityRuleModalProps {
  isOpen: boolean
  onClose: () => void
  rule: SecurityRule | null
  onSaved: () => void
}

interface FormState {
  name: string
  description: string
  type: RuleType
  priority: number
  action: string
  redirectUrl: string
  enabled: boolean
  config: Record<string, any>
}

// ── Tag Input Component ──────────────────────────────────────────

function TagInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[]
  onChange: (vals: string[]) => void
  placeholder: string
}) {
  const [input, setInput] = useState('')

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault()
      const trimmed = input.trim()
      if (!values.includes(trimmed)) {
        onChange([...values, trimmed])
      }
      setInput('')
    }
  }

  const removeTag = (index: number) => {
    onChange(values.filter((_, i) => i !== index))
  }

  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {values.map((val, idx) => (
          <span
            key={idx}
            className="inline-flex items-center gap-1 rounded bg-indigo-100 px-2 py-0.5 font-mono text-xs text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
          >
            {val}
            <button
              type="button"
              onClick={() => removeTag(idx)}
              className="hover:text-red-500"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
      />
    </div>
  )
}

// ── Config Form per Rule Type ────────────────────────────────────

function ConfigForm({
  type,
  config,
  onChange,
}: {
  type: RuleType
  config: Record<string, any>
  onChange: (cfg: Record<string, any>) => void
}) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const set = (key: string, value: any) => onChange({ ...config, [key]: value })

  switch (type) {
    case 'rate_limit':
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Max Requests
            </label>
            <input
              type="number"
              min={1}
              max={10000}
              value={config.maxRequests ?? 100}
              onChange={e => set('maxRequests', parseInt(e.target.value) || 0)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Time Window (seconds)
            </label>
            <input
              type="number"
              min={1}
              max={3600}
              value={config.timeWindow ?? 60}
              onChange={e => set('timeWindow', parseInt(e.target.value) || 0)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      )

    case 'ip_block':
      return (
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Blocked IPs
            </label>
            <TagInput
              values={config.blockedIps ?? []}
              onChange={v => set('blockedIps', v)}
              placeholder="Enter IP and press Enter (e.g. 192.168.1.1)"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Allowed IPs
            </label>
            <TagInput
              values={config.allowedIps ?? []}
              onChange={v => set('allowedIps', v)}
              placeholder="Enter IP and press Enter"
            />
          </div>
        </div>
      )

    case 'pattern_match':
      return (
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              String Patterns
            </label>
            <TagInput
              values={config.patterns ?? []}
              onChange={v => set('patterns', v)}
              placeholder="Enter pattern and press Enter"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Regex Patterns (one per line)
            </label>
            <textarea
              rows={4}
              value={(config.regexPatterns ?? []).join('\n')}
              onChange={e =>
                set(
                  'regexPatterns',
                  e.target.value.split('\n').filter(l => l.trim())
                )
              }
              placeholder="^https?:\/\/.*\.tk\/.*"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      )

    case 'geo_block':
      return (
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Blocked Countries (2-letter code)
            </label>
            <TagInput
              values={config.blockedCountries ?? []}
              onChange={v =>
                set(
                  'blockedCountries',
                  v.map(c => c.toUpperCase())
                )
              }
              placeholder="e.g. CN, RU"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Allowed Countries (2-letter code)
            </label>
            <TagInput
              values={config.allowedCountries ?? []}
              onChange={v =>
                set(
                  'allowedCountries',
                  v.map(c => c.toUpperCase())
                )
              }
              placeholder="e.g. KR, US, JP"
            />
          </div>
        </div>
      )

    case 'malware_detection':
      return (
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.scanEnabled !== false}
              onChange={e => set('scanEnabled', e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Enable Google Safe Browsing scan
          </label>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Scan Timeout (ms)
            </label>
            <input
              type="number"
              min={1000}
              max={30000}
              step={500}
              value={config.scanTimeout ?? 5000}
              onChange={e =>
                set('scanTimeout', parseInt(e.target.value) || 5000)
              }
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      )

    case 'phishing_protection':
      return (
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Phishing Domains
            </label>
            <TagInput
              values={config.phishingDomains ?? []}
              onChange={v => set('phishingDomains', v)}
              placeholder="e.g. fake-bank.net"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Suspicious Keywords
            </label>
            <TagInput
              values={config.suspiciousKeywords ?? []}
              onChange={v => set('suspiciousKeywords', v)}
              placeholder="e.g. phishing, scam"
            />
          </div>
        </div>
      )

    default:
      return null
  }
}

// ── Default config per type ──────────────────────────────────────

function getDefaultConfig(type: RuleType): Record<string, any> {
  switch (type) {
    case 'rate_limit':
      return { maxRequests: 100, timeWindow: 60 }
    case 'ip_block':
      return { blockedIps: [], allowedIps: [] }
    case 'pattern_match':
      return { patterns: [], regexPatterns: [] }
    case 'geo_block':
      return { blockedCountries: [], allowedCountries: [] }
    case 'malware_detection':
      return { scanEnabled: true, scanTimeout: 5000 }
    case 'phishing_protection':
      return { phishingDomains: [], suspiciousKeywords: [] }
    default:
      return {}
  }
}

// ── Modal Component ──────────────────────────────────────────────

export default function SecurityRuleModal({
  isOpen,
  onClose,
  rule,
  onSaved,
}: SecurityRuleModalProps) {
  const isEdit = rule !== null

  const [form, setForm] = useState<FormState>({
    name: '',
    description: '',
    type: 'rate_limit',
    priority: 50,
    action: 'block',
    redirectUrl: '',
    enabled: true,
    config: getDefaultConfig('rate_limit'),
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (rule) {
      setForm({
        name: rule.name,
        description: rule.description,
        type: rule.type,
        priority: rule.priority ?? 50,
        action: rule.action ?? 'block',
        redirectUrl: rule.redirectUrl ?? '',
        enabled: rule.enabled,
        config: { ...rule.config },
      })
    } else {
      setForm({
        name: '',
        description: '',
        type: 'rate_limit',
        priority: 50,
        action: 'block',
        redirectUrl: '',
        enabled: true,
        config: getDefaultConfig('rate_limit'),
      })
    }
  }, [rule, isOpen])

  const handleTypeChange = (newType: RuleType) => {
    setForm(prev => ({
      ...prev,
      type: newType,
      config: getDefaultConfig(newType),
    }))
  }

  const handleSave = useCallback(async () => {
    if (!form.name.trim() || !form.description.trim()) {
      toast.error('Name and description are required')
      return
    }

    setSaving(true)
    try {
      const token = adminAuthService.getToken()
      if (!token) {
        throw new Error('No admin token')
      }

      const body = {
        name: form.name.trim(),
        description: form.description.trim(),
        type: form.type,
        config: form.config,
        priority: form.priority,
        action: form.action,
        enabled: form.enabled,
        redirectUrl: form.action === 'redirect' ? form.redirectUrl : undefined,
      }

      const url = isEdit
        ? `/api/admin/security/rules/${rule._id}`
        : '/api/admin/security/rules'

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to save rule')
      }

      toast.success(isEdit ? 'Rule updated' : 'Rule created')
      onSaved()
      onClose()
    } catch (error: any) {
      toast.error(error.message || 'Failed to save rule')
    } finally {
      setSaving(false)
    }
  }, [form, isEdit, rule, onSaved, onClose])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-2xl dark:bg-gray-800"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {isEdit ? 'Edit Security Rule' : 'Create Security Rule'}
              </h3>
              <button
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-4 px-6 py-4">
              {/* Name */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Name
                </label>
                <input
                  value={form.name}
                  onChange={e =>
                    setForm(prev => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g. Rate Limiting - API"
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Description */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="What does this rule do?"
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Type (disabled in edit mode) */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Rule Type
                </label>
                <select
                  value={form.type}
                  onChange={e => handleTypeChange(e.target.value as RuleType)}
                  disabled={isEdit}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {RULE_TYPES.map(t => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Priority & Action */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Priority (0-100)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={form.priority}
                    onChange={e =>
                      setForm(prev => ({
                        ...prev,
                        priority: parseInt(e.target.value) || 0,
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Action
                  </label>
                  <select
                    value={form.action}
                    onChange={e =>
                      setForm(prev => ({ ...prev, action: e.target.value }))
                    }
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    {ACTIONS.map(a => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Redirect URL (only when action is redirect) */}
              {form.action === 'redirect' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Redirect URL
                  </label>
                  <input
                    value={form.redirectUrl}
                    onChange={e =>
                      setForm(prev => ({
                        ...prev,
                        redirectUrl: e.target.value,
                      }))
                    }
                    placeholder="https://example.com/blocked"
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              )}

              {/* Enabled toggle */}
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={e =>
                    setForm(prev => ({ ...prev, enabled: e.target.checked }))
                  }
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                Enabled
              </label>

              {/* Dynamic config section */}
              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <h4 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Configuration
                </h4>
                <ConfigForm
                  type={form.type}
                  config={form.config}
                  onChange={c => setForm(prev => ({ ...prev, config: c }))}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
              <button
                onClick={onClose}
                className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Rule'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
